import { api } from "./api_axios";

// ---- auth helpers ----
export const getCsrf = async () => (await api.get('/auth/csrf/')).data;
export const getMe = async () => (await api.get('/auth/me/')).data;

// dj-rest-auth login accepts username or email + password. Phone numbers are
// sent as the username; the backend's PhoneNumberBackend recognises them.
export const postLogin = async ({ identifier, password }) => {
  const isEmail = /\S+@\S+\.\S+/.test(identifier);
  const payload = isEmail
    ? { email: identifier, password }
    : { username: identifier, password };
  const { data } = await api.post('/auth/login/', payload);
  return data; // session cookie is set by the response
};

export const postLogout = async () => (await api.post('/auth/logout/', {})).data;

// Registration needs an email, a phone number, or both.
export const postRegister = async ({ username, email, phoneNumber, password1, password2 }) => {
  const { data } = await api.post('/auth/registration/', {
    username, email, phone_number: phoneNumber, password1, password2,
  });
  return data;
};

// Email users confirm the link they were mailed; phone users enter an SMS code.
export const postResendVerificationEmail = async (email) =>
  (await api.post('/auth/registration/resend-email/', { email })).data;
export const postVerifyEmail = async (key) =>
  (await api.post('/auth/registration/verify-email/', { key })).data;
// A number is sent only by users who have none saved yet (they signed up with an email address).
export const postPhoneSendCode = async (phoneNumber) =>
  (await api.post('/auth/phone/send-code/', phoneNumber ? { phone_number: phoneNumber } : {})).data;
export const postPhoneVerify = async (code) => (await api.post('/auth/phone/verify/', { code })).data;

// Sign-in and password reset by SMS code, on servers with phone numbers. Requesting a code
// answers the same way whether or not an account uses the number.
export const postPhoneLoginSendCode = async (phoneNumber) =>
  (await api.post('/auth/phone/login/send-code/', { phone_number: phoneNumber })).data;
export const postPhoneLogin = async ({ phoneNumber, code }) =>
  (await api.post('/auth/phone/login/', { phone_number: phoneNumber, code })).data;
export const postPasswordResetSendCode = async (phoneNumber) =>
  (await api.post('/auth/phone/password-reset/send-code/', { phone_number: phoneNumber })).data;
// Sets the new password and signs the user in.
export const postPasswordResetConfirm = async ({ phoneNumber, code, password1, password2 }) =>
  (await api.post('/auth/phone/password-reset/', {
    phone_number: phoneNumber, code, new_password1: password1, new_password2: password2,
  })).data;

// ---- Referral Helpers ----
export const getReferralSummary = async () => (await api.get('/referrals/')).data;
export const postReferralCode = async () => (await api.post('/referrals/code/', {})).data;

// ---- Game Helpers ----
export async function getGames(params = {}) {
  const { data } = await api.get('/games/games/', { params });
  return data;
}

export async function getAllGames(params = {}) {
  const games = [];
  let url = '/games/games/';
  let requestParams = { ordering: 'title', ...params };
  while (url) {
    const { data } = await api.get(url, { params: requestParams });
    games.push(...(data.results || data || []));
    url = data.next || null;
    requestParams = undefined;
  }
  return games;
}

export async function getSubscriptionPlans(params = {}) {
  const { data } = await api.get('/subscriptions/plans/', { params });
  return data;
}

export async function getSubscriptions(params = {}) {
  const { data } = await api.get('/subscriptions/subscriptions/', { params });
  return data;
}

export async function getGameOwnerships(params = {}) {
  const { data } = await api.get('/games/ownerships/', { params });
  return data;
}

export async function getPlayHistory() {
  const sessions = [];
  let url = '/games/sessions/history/';
  while (url) {
    const { data } = await api.get(url);
    sessions.push(...(data.results || data || []));
    url = data.next || null;
  }
  return sessions;
}

export async function getTournamentHistory() {
  const runs = [];
  let url = '/games/tournament-runs/history/';
  while (url) {
    const { data } = await api.get(url);
    runs.push(...(data.results || data || []));
    url = data.next || null;
  }
  return runs;
}

// ---- Survey helpers ----
export async function getPocketPlaySurvey() {
  const { data } = await api.get('/surveys/pocketplay/');
  return data;
}

export async function savePocketPlaySurvey({ answers, action, expectedRevision }) {
  const { data } = await api.put('/surveys/pocketplay/', {
    answers,
    action,
    expected_revision: expectedRevision,
  });
  return data;
}

export async function postSubscriptionCheckout({
  planId,
  billingInterval,
  successUrl,
  cancelUrl,
  mobile,
  discountCode,
}) {
  const { data } = await api.post('/payments/checkout/subscription/', {
    plan_id: planId,
    billing_interval: billingInterval,
    success_url: successUrl,
    cancel_url: cancelUrl,
    ...checkoutDetails({ mobile, discountCode }),
  });
  return data;
}

export async function postGameCheckout({
  gameId,
  successUrl,
  cancelUrl,
  mobile,
  discountCode,
}) {
  const { data } = await api.post('/payments/checkout/game/', {
    game_id: gameId,
    success_url: successUrl,
    cancel_url: cancelUrl,
    ...checkoutDetails({ mobile, discountCode }),
  });
  return data;
}

// Manual renewal (Zarinpal): pays the next period of the active subscription.
export async function postSubscriptionRenew({
  successUrl,
  cancelUrl,
  mobile,
  discountCode,
}) {
  const { data } = await api.post('/payments/subscription/renew/', {
    success_url: successUrl,
    cancel_url: cancelUrl,
    ...checkoutDetails({ mobile, discountCode }),
  });
  return data;
}

export async function postCancelSubscription() {
  const { data } = await api.post('/payments/subscription/cancel/', {});
  return data;
}

export async function postSubscriptionChange({
  planId,
  billingInterval,
}) {
  const { data } = await api.post('/payments/subscription/change/', {
    plan_id: planId,
    billing_interval: billingInterval,
  });
  return data;
}

// Optional fields collected by the checkout dialog (Stripe only asks for a referral code).
function checkoutDetails({ mobile, discountCode }) {
  return {
    ...(mobile ? { mobile } : {}),
    ...(discountCode ? { discount_code: discountCode } : {}),
  };
}

export async function getGame(id) {
  const { data } = await api.get(`/games/games/${id}/`);
  return data;
}

export async function getGameByTitle(title) {
  const { data } = await api.get(`/games/games/`, { params: { title } });
  console.log('[API] getGameByTitle:', title, '->', data);
  return data.results[0] || null;
}

export async function getGameMusic(gameId) {
  const { data } = await api.get(`/music/tracks/for-game/${gameId}/`);
  return data;
}

export async function postGameAccess(params) {
  const gameId = typeof params === "object" ? params.gameId : params;
  const clientSessionKey = typeof params === "object" ? params.clientSessionKey : null;
  const { data } = await api.post(`/games/games/${gameId}/request-access/`, {
    ...(clientSessionKey ? { client_session_key: clientSessionKey } : {}),
  }, {
    timeout: 20000,
  });
  return data;
}

// The server only lets a guest control its session with the key that created it.
function sessionOwnerFields(clientSessionKey) {
  return clientSessionKey ? { client_session_key: clientSessionKey } : {};
}

export async function postSessionEnd(sessionId, clientSessionKey = null) {
  const { data } = await api.post(`/games/sessions/end-session/`, {
    sessionId,
    ...sessionOwnerFields(clientSessionKey),
  });
  return data;
}

export async function postSessionKeepAlive(sessionId, gameInfo = null, clientSessionKey = null) {
  const { data } = await api.post(`/games/sessions/keep-alive/`, {
    sessionId,
    gameInfo,
    ...sessionOwnerFields(clientSessionKey),
  });
  return data;
}

export async function postSessionGameFinished(sessionId, gameInfo = null, clientSessionKey = null) {
  const { data } = await api.post(`/games/sessions/game-finished/`, {
    sessionId,
    gameInfo,
    ...sessionOwnerFields(clientSessionKey),
  });
  return data;
}

// ---- Saved games (save & continue) ----
export async function getGameSaves(gameId) {
  const { data } = await api.get(`/games/saves/`, { params: { game: gameId } });
  return data;
}

export async function getGameSave(saveId) {
  const { data } = await api.get(`/games/saves/${saveId}/`);
  return data;
}

export async function postGameSave(sessionId, envelope) {
  const { data } = await api.post(`/games/saves/`, { sessionId, envelope });
  return data;
}

export async function deleteGameSave(saveId) {
  await api.delete(`/games/saves/${saveId}/`);
}

// ---- Tournament helpers ----
export async function getTournaments(params = {}) {
  return (await api.get('/games/tournaments/', { params })).data;
}

export async function getAllTournaments() {
  const tournaments = [];
  let url = '/games/tournaments/';
  let params = { ordering: 'name' };
  while (url) {
    const { data } = await api.get(url, { params });
    tournaments.push(...(data.results || data || []));
    url = data.next || null;
    params = undefined;
  }
  return tournaments;
}

export async function getTournament(id) {
  return (await api.get(`/games/tournaments/${id}/`)).data;
}

export async function createTournament(payload) {
  return (await api.post('/games/tournaments/', payload)).data;
}

export async function updateTournament(id, payload) {
  return (await api.put(`/games/tournaments/${id}/`, payload)).data;
}

export async function startTournamentRun(tournamentId) {
  return (await api.post(`/games/tournaments/${tournamentId}/start-run/`, {})).data;
}

export async function getTournamentRun(runId) {
  return (await api.get(`/games/tournament-runs/${runId}/`)).data;
}

export async function startTournament(runId) {
  return (await api.post(`/games/tournament-runs/${runId}/start/`, {})).data;
}

export async function getTournamentHostTicket(runId) {
  return (await api.post(`/games/tournament-runs/${runId}/host-ticket/`, {})).data;
}

export async function prepareTournamentRound(runId, restart = false) {
  return (await api.post(`/games/tournament-runs/${runId}/prepare-round/`, { restart })).data;
}

export async function finishTournamentRound(runId, sessionId, gameInfo) {
  return (await api.post(`/games/tournament-runs/${runId}/round-finished/`, {
    sessionId,
    gameInfo,
  })).data;
}

export async function createRecoveryCode(runId, controllerId) {
  return (await api.post(`/games/tournament-runs/${runId}/new-recovery-code/`, { controllerId })).data;
}

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
}

function buildApiUrl(path) {
  const base = String(api.defaults.baseURL || "").replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${suffix}` : suffix;
}

export function postSessionEndBestEffort(sessionId, clientSessionKey = null) {
  if (!sessionId || typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const url = buildApiUrl("/games/sessions/end-session/");
  const csrfToken = getCookie("csrftoken");

  try {
    window
      .fetch(url, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
        },
        body: JSON.stringify({ sessionId, ...sessionOwnerFields(clientSessionKey) }),
      })
      .catch(() => {});
  } catch {}
}
